import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { personalInfoService, PersonalInfo } from '../../../../services/personalInfo';
import { API_URL } from '../../../../config';
 

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('PersonalInfoInput');

type RouteParams = {
  taskId: number;
};

// Constants for dropdown options
const FEET_OPTIONS = Array.from({ length: 4 }, (_, i) => (i + 4).toString());
const INCHES_OPTIONS = Array.from({ length: 12 }, (_, i) => i.toString());
const WEIGHT_OPTIONS = Array.from({ length: 301 }, (_, i) => (i + 100).toString()).concat(['400+']);
const EYE_COLOR_OPTIONS = ['Brown', 'Blue', 'Green', 'Hazel'];
const HAIR_COLOR_OPTIONS = ['Brown', 'Blonde', 'Red', 'Black', 'Gray', 'Other'];
const BLOOD_TYPE_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

export const PersonalInfoInputScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const taskId = route.params?.taskId || 1;

  const getTaskTitle = (id: number) => {
    switch (id) {
      case 1: return 'Identifying Information';
      case 2: return 'Employment Information';
      case 3: return 'Spouse Information';
      case 4: return 'Children Information';
      case 5: return 'Pet Information';
      default: return 'Personal Information';
    }
  };

  const getTaskDescription = (id: number) => {
    switch (id) {
      case 1: return 'Please provide your basic identifying information.';
      case 2: return 'Please provide your employment information.';
      case 3: return 'Please provide your spouse\'s information.';
      case 4: return 'Please provide your children\'s information.';
      case 5: return 'Please provide your pet\'s information.';
      default: return 'Please provide your personal information.';
    }
  };

  const [formData, setFormData] = useState({
    feet: '',
    inches: '',
    weight: '',
    eyeColor: '',
    hairColor: '',
    bloodType: '',
    identifyingMarks: '',
  });

  // State for dropdown modal
  const [modalVisible, setModalVisible] = useState(false);
  const [activeField, setActiveField] = useState<string>('');
  const [activeOptions, setActiveOptions] = useState<string[]>([]);
  
  // State for loading and form submission
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [existingPersonalInfo, setExistingPersonalInfo] = useState<PersonalInfo | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleDropdownPress = (field: string) => {
    let options: string[] = [];
    switch (field) {
      case 'feet':
        options = FEET_OPTIONS;
        break;
      case 'inches':
        options = INCHES_OPTIONS;
        break;
      case 'weight':
        options = WEIGHT_OPTIONS;
        break;
      case 'eyeColor':
        options = EYE_COLOR_OPTIONS;
        break;
      case 'hairColor':
        options = HAIR_COLOR_OPTIONS;
        break;
      case 'bloodType':
        options = BLOOD_TYPE_OPTIONS;
        break;
    }
    setActiveField(field);
    setActiveOptions(options);
    setModalVisible(true);
  };

  const handleOptionSelect = (option: string) => {
    setFormData({ ...formData, [activeField]: option });
    setModalVisible(false);
  };

  // Load existing personal information
  useEffect(() => {
    loadPersonalInfo();
  }, [taskId]);

  const loadPersonalInfo = async () => {
    try {
      setInitialLoading(true);
      const data = await personalInfoService.getPersonalInfo(taskId);
      if (data && data.length > 0) {
        const personalInfo = data[0];
        setExistingPersonalInfo(personalInfo);
        setIsEditing(true);
        setFormData({
          feet: personalInfo.feet?.toString() || '',
          inches: personalInfo.inches?.toString() || '',
          weight: personalInfo.weight?.toString() || '',
          eyeColor: personalInfo.eyeColor || '',
          hairColor: personalInfo.hairColor || '',
          bloodType: personalInfo.bloodType || '',
          identifyingMarks: personalInfo.identifyingMarks || '',
        });
      }
    } catch (error) {
      logger.error('Error loading personal info:', error);
      // Don't show alert for missing data - just means no data exists yet
    } finally {
      setInitialLoading(false);
    }
  };

  const savePersonalInfo = async () => {
    try {
      setLoading(true);
      
      // Convert string values to numbers where needed
      const personalInfoData = {
        taskId,
        feet: parseInt(formData.feet) || 0,
        inches: parseInt(formData.inches) || 0,
        weight: parseInt(formData.weight) || 0,
        eyeColor: formData.eyeColor,
        hairColor: formData.hairColor,
        bloodType: formData.bloodType,
        identifyingMarks: formData.identifyingMarks,
      };
      
      if (isEditing && existingPersonalInfo) {
        // Update existing record
        await personalInfoService.updatePersonalInfo(existingPersonalInfo.id, personalInfoData);
      } else {
        // Create new record
        await personalInfoService.createPersonalInfo(personalInfoData);
      }
      
      return true;
    } catch (error) {
      logger.error('Error saving personal info:', error);
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to save personal information. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const success = await savePersonalInfo();
    if (success) {
      (navigation as any).navigate('PersonalInfoLockUnlock', { taskId });
    }
  };

  if (initialLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <ActivityIndicator size="large" color="#FFFFFF" />
          <Text style={[styles.subtitle, { marginTop: 20 }]}>Loading personal information...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>{getTaskTitle(taskId)}</Text>
        <Text style={styles.subtitle}>{getTaskDescription(taskId)}</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets={true}
        >
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Height</Text>
            <View style={styles.heightContainer}>
              <View style={styles.heightDropdown}>
                <Text style={styles.heightLabel}>Feet</Text>
                <TouchableOpacity
                  style={styles.dropdownButton}
                  onPress={() => handleDropdownPress('feet')}
                >
                  <Text style={styles.dropdownButtonText}>
                    {formData.feet || 'ft'}
                  </Text>
                  <Image
                    source={require('../../../../assets/icons/down.png')}
                    style={styles.dropdownIcon}
                  />
                </TouchableOpacity>
              </View>

              <View style={styles.heightDropdown}>
                <Text style={styles.heightLabel}>Inches</Text>
                <TouchableOpacity
                  style={styles.dropdownButton}
                  onPress={() => handleDropdownPress('inches')}
                >
                  <Text style={styles.dropdownButtonText}>
                    {formData.inches || 'in'}
                  </Text>
                  <Image
                    source={require('../../../../assets/icons/down.png')}
                    style={styles.dropdownIcon}
                  />
                </TouchableOpacity>
              </View>
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Weight (lbs)</Text>
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => handleDropdownPress('weight')}
            >
              <Text style={styles.dropdownButtonText}>
                {formData.weight || 'Select weight'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Eye Color</Text>
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => handleDropdownPress('eyeColor')}
            >
              <Text style={styles.dropdownButtonText}>
                {formData.eyeColor || 'Select eye color'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Hair Color</Text>
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => handleDropdownPress('hairColor')}
            >
              <Text style={styles.dropdownButtonText}>
                {formData.hairColor || 'Select hair color'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Blood Type</Text>
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => handleDropdownPress('bloodType')}
            >
              <Text style={styles.dropdownButtonText}>
                {formData.bloodType || 'Select blood type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Identifying Marks</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.identifyingMarks}
              onChangeText={(text) => setFormData({ ...formData, identifyingMarks: text })}
              placeholder="Enter any identifying marks"
              multiline
              numberOfLines={4}
            />
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.submitButton, loading && { opacity: 0.7 }]}
              onPress={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.submitButtonText}>Submit</Text>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setModalVisible(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select {activeField}</Text>
              <TouchableOpacity onPress={() => setModalVisible(false)}>
                <Text style={styles.modalCloseButton}>Done</Text>
              </TouchableOpacity>
            </View>
            <ScrollView>
              {activeOptions.map((option) => (
                <TouchableOpacity
                  key={option}
                  style={styles.optionItem}
                  onPress={() => handleOptionSelect(option)}
                >
                  <Text
                    style={[
                      styles.optionText,
                      formData[activeField as keyof typeof formData] === option && styles.selectedOptionText,
                    ]}
                  >
                    {option}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    marginBottom: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(10),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
    marginHorizontal: ms(20),
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    textAlign: 'center',
    marginHorizontal: ms(20),
    marginTop: ms(5),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(30),
    paddingHorizontal: ms(20),
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
        borderColor: '#d8dbdd',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  inputLabel: {
    fontSize: ms(14, 0.3),
    color: '#666',
    marginBottom: ms(5),
  },
  dropdownButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: ms(1),
        borderColor: '#d8dbdd',
  },
  dropdownButtonText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#666',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E5E5E5',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
  },
  optionItem: {
    padding: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E5E5E5',
  },
  optionText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  selectedOptionText: {
    color: '#29BFC0',
    fontWeight: '600',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  heightContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: ms(15),
  },
  heightDropdown: {
    flex: 1,
  },
  heightLabel: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
}); 